import type { ContentLocationItem, KcActivitiesByDayItem, KcActivityItem } from '../types/studentKcProgress';
import { KC_ACTIVITY_OUTCOME, KC_DETAIL_SOURCE, type KcActivityOutcome, type KcDetailSource } from '../types/studentKcProgress.constants';

import { toLocalYmd } from './formatUtils';
import { formatLocations, getLessonName } from './radarUtils';

/**
 * Format an array of ContentLocationItem (response of /stats/users/:userId/kcs/:kcId/progress)
 * into readable strings of the form "Unit – Lesson", stripping HTML and ignoring lessons
 * without a name.
 */
export function formatContentLocations(items: ContentLocationItem[]): string[] {
  return formatLocations(items);
}

function buildStudentKcProgressPath(userId: string, kcId: string, lessonId?: string): string {
  const base = `/radar/students/${encodeURIComponent(userId)}/kcs/${encodeURIComponent(kcId)}`;

  if (lessonId) {
    return `${base}/lessons/${encodeURIComponent(lessonId)}`;
  }

  return base;
}

const KC_DETAIL_SOURCE_PARAM = 'source';
const KC_DETAIL_SOURCES: readonly string[] = Object.values(KC_DETAIL_SOURCE);

type StudentKcProgressUrlParams = {
  kcId: string;
  lessonId?: string;
  /** Query the link already carries (breadcrumb section, programme); the source is added to it. */
  search?: string;
  source: KcDetailSource;
  userId: string;
};

// The source is required so that every link to the page says where it comes from. It is its own
// parameter instead of a new value of `from`: `from` is the breadcrumb section and is carried on to
// the pages opened from this one, while the source only describes the click that led here.
export function buildStudentKcProgressUrl({ kcId, lessonId, search = '', source, userId }: StudentKcProgressUrlParams): string {
  const params = new URLSearchParams(search);
  params.set(KC_DETAIL_SOURCE_PARAM, source);

  return `${buildStudentKcProgressPath(userId, kcId, lessonId)}?${params.toString()}`;
}

export function parseKcDetailSource(search: string): KcDetailSource | null {
  const source = new URLSearchParams(search).get(KC_DETAIL_SOURCE_PARAM);

  return source !== null && KC_DETAIL_SOURCES.includes(source) ? (source as KcDetailSource) : null;
}

export function resolveLessonLabel(locations: ContentLocationItem[], lessonId?: string): string | null {
  const fromRoute = lessonId ? getLessonName(locations.find((loc) => loc.lesson?.id === lessonId)) : '';
  if (fromRoute) {
    return fromRoute;
  }

  for (const loc of locations) {
    const name = getLessonName(loc);
    if (name) {
      return name;
    }
  }

  return null;
}

/**
 * Classify a KC activity by its score. An activity is considered correct only when
 * its score reaches the maximum (1); any score below 1 is incorrect, and a null
 * score means the activity was not answered.
 */
export function getActivityOutcome(activity: KcActivityItem): KcActivityOutcome {
  if (activity.score === null) return KC_ACTIVITY_OUTCOME.UNANSWERED;
  return isActivityCorrect(activity) ? KC_ACTIVITY_OUTCOME.CORRECT : KC_ACTIVITY_OUTCOME.INCORRECT;
}

/**
 * Returns true when a graded activity reached the maximum score (1). Accepts any
 * shape with a nullable `score`, so it works for both `KcActivityItem` (listing)
 * and `ActivityInstanceDetail` (drawer).
 */
export function isActivityCorrect(activity: KcActivityItem): boolean {
  return activity.score === 1;
}

/**
 * Group a flat list of KC activities by the calendar day of `respondedAt` in the browser
 * local timezone (`YYYY-MM-DD`). Preserves the input order, so when the source is sorted
 * DESC by `respondedAt` the resulting groups are day-DESC and within-day DESC for free —
 * no extra sort needed. The UTC→local conversion is monotonic with respect to the instant,
 * so consecutive runs remain valid.
 */
export function groupActivitiesByDay(activities: KcActivityItem[]): KcActivitiesByDayItem[] {
  const days: KcActivitiesByDayItem[] = [];
  for (const activity of activities) {
    const date = toLocalYmd(new Date(activity.respondedAt));
    const lastDay = days[days.length - 1];
    if (lastDay?.date === date) {
      lastDay.activities.push(activity);
    } else {
      days.push({ date, activities: [activity] });
    }
  }
  return days;
}
